import React, { useState, useEffect } from 'react';
import {
  clearImportTransaction,
  getIngestedTransactionById,
} from '../../actions/keyCodeActions';
import IngestedTransactionDetails from './components/IngestedTransactionDetails';
import KeyProcessProgressBar from './components/KeyProcessProgressBar';
import ActionBar from '../layout/ActionBar';
import RemoveIngestedKeys from './slideout/RemoveIngestedKeys';
import Loading from '../common/Loading';
import { toast } from 'react-toastify';
import SlidingPane from 'react-sliding-pane';
import Button from '../common/Button';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../../util/pageTitle';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks';
import {
  addToGroup,
  removeFromGroup,
} from '../../middleware/signalRMiddleware';
import KwPanel from '../common/KwPanel';

function IngestedTransaction() {
  const { id } = useParams<{ id: string }>();
  const [, setPageTitle] = usePageTitle(['Ingested transaction', 'Keys']);
  const currentUser = useAppSelector((state) => state.auth);
  const importTransactions = useAppSelector((state) => state.importTransactions);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [transaction, setTransaction] = useState<any>();
  const [lastUpdate] = useState(new Date());
  const [removeIngestedKeysSlideoutOpen, setRemoveIngestedKeysSlideoutOpen] =
    useState(false);

  useEffect(() => {
    if (id) {
      getIngestedTransactionById(id)
        .then((d) => {
          setTransaction(d);
          setPageTitle([d.keyProviderName ?? 'Ingested transaction', 'Ingested transaction', 'Keys']);
        })
        .catch(() => {
          toast.error(t('Transaction not found'));
          navigate('/keys/ingested-batches');
        });
    }
  }, [id, lastUpdate, t, setPageTitle, navigate]);

  useEffect(() => {
    if (transaction && transaction.status === 'Pending') {
      dispatch(addToGroup(`import:${transaction.id}`));
      return () => {
        dispatch(removeFromGroup(`import:${transaction.id}`));
      };
    }
  }, [transaction, dispatch]);

  useEffect(() => {
    if (
      importTransactions?.length > 0 &&
      importTransactions.find((x: any) => x.id === id)
    ) {
      const updated = importTransactions.find((x: any) => x.id === id);
      setTransaction((prevState: any) => ({
        ...prevState,
        ...updated,
      }));
      return () => dispatch(clearImportTransaction(id));
    }
  }, [importTransactions, id, dispatch]);

  const getToolTip = () => {
    if (!transaction) return '';
    if (transaction.requestId != null) return t('Already assigned to a request');
    if (transaction.keysDirty) return t('Keys have already been allocated');
    if (transaction.codesImported !== true) return t('Keys are being processed and cannot be stopped');
    return '';
  };

  return transaction ? (
    <>
      <ActionBar
        breadcrumb={[
          { link: '/keys/ingested-batches', label: t('Ingested batches') },
          { label: t('Ingested transaction') },
        ]}
      >
        {!transaction.keysDirty ? (
          <Button
            className="btn btn-primary"
            onClick={() => setRemoveIngestedKeysSlideoutOpen(true)}
            text={t('Remove')}
          />
        ) : (
          <Button
            className="btn btn-primary"
            isDisabled={true}
            title={getToolTip()}
            text={t('Remove')}
          />
        )}
      </ActionBar>
      <IngestedTransactionDetails
        transaction={transaction}
        currentUser={currentUser}
      />
      <KeyProcessProgressBar transaction={transaction} />
      {transaction.badKeys && (
        <KwPanel className="mb-4">
          <header className="mb-2">
            <h2 className="no-margin">{t('Invalid Keys')}</h2>
          </header>
          <ul>
            {transaction.badKeys.split('|').map((x: any, i: any) => (
              <li key={i}>{x}</li>
            ))}
          </ul>
        </KwPanel>
      )}

      <SlidingPane
        isOpen={removeIngestedKeysSlideoutOpen}
        hideHeader={true}
        from="right"
        className="side-panel"
        onRequestClose={() => setRemoveIngestedKeysSlideoutOpen(false)}
      >
        <RemoveIngestedKeys
          id={transaction.id}
          done={() => navigate('/keys/ingested-batches')}
          cancel={() => setRemoveIngestedKeysSlideoutOpen(false)}
        />
      </SlidingPane>
    </>
  ) : (
    <Loading />
  );
}

export default IngestedTransaction;
